import { useState } from 'react'

/**
 * "How does a battle work?" disclosure — introduces the three phases and the
 * armure/coque concepts in plain naval terms, so a player understands a raid
 * (and its replay) before ever seeing one. Drop-in anywhere: it carries its
 * own toggle.
 */
export function CombatPrimer({ defaultOpen = false }: { defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div className="text-xs">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-ocean-300 hover:text-sand-100 transition"
      >
        <span>{open ? '▾' : '▸'}</span>
        ❓ Comment se déroule un combat ?
      </button>

      {open && (
        <div className="mt-2 space-y-2 bg-ocean-900/40 rounded p-3 border border-ocean-500/20 text-ocean-200 leading-relaxed">
          <p>
            Une bataille se joue en <strong className="text-sand-200">trois phases</strong>, dans l'ordre :
          </p>
          <ul className="space-y-1">
            <li>🧭 <strong>Manœuvre</strong> : les flottes cherchent le vent. Celle qui manœuvre le mieux <em>tire la première</em> à la bordée.</li>
            <li>💥 <strong>Bordée</strong> : échange de canons à distance. Chaque coup au but entame une coque ennemie.</li>
            <li>⚔️ <strong>Abordage</strong> : corps à corps final sur les navires encore à flot.</li>
          </ul>
          <p className="pt-1">
            🛡️ <strong>Armure</strong> = blindage. Tes navires doivent l'égaler pour percer la coque adverse,
            sinon les boulets <em>ricochent</em> (un brick ne percera jamais un galion).<br />
            ❤️ <strong>Coque</strong> = points de vie : le nombre de coups encaissés avant de sombrer.
          </p>
          <p className="pt-1">
            🏆 <strong>Gagner ≠ rentrer indemne.</strong> Une victoire veut seulement dire que tu as{' '}
            <strong>coulé toute la flotte adverse</strong> ; tu peux l'emporter à 100 % et perdre tout de
            même un ou deux navires au passage. Tes <strong>coques les moins blindées</strong> sont visées
            en premier : un petit navire bon marché peut servir de <em>bouclier</em> pour épargner les gros.
            La « perte estimée » affichée avant un raid est une <em>fourchette</em> : l'issue réelle y tombe
            le plus souvent, mais peut la dépasser dans les mauvais jours.
          </p>
          <p className="pt-1">
            🔧 <strong>Navire endommagé ?</strong> Une coque entamée pendant la bataille n'a rien de définitif :
            un navire qui <strong>survit rentre intact</strong> au port, prêt à repartir. Pas d'avaries qui
            traînent, aucune réparation à payer. Seul compte le fait de couler ou non.
          </p>
          <p className="text-rum-300">
            ⚠️ Un navire coulé, lui, est <strong>perdu définitivement</strong>. Compose ta flotte en conséquence.
          </p>
        </div>
      )}
    </div>
  )
}
